import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { RoutableEntityType, EntityParamsMap } from '@ruta/shared';
import { entityPath, parseEntityPath } from '@ruta/shared';
import { resolveEntityRoute } from '@/lib/entityRoutes';

export function useEntityNavigation() {
  const navigate = useNavigate();

  const navigateToEntity = useCallback(
    <T extends RoutableEntityType>(type: T, params: EntityParamsMap[T]) => {
      const ref = { type, params } as { type: T; params: EntityParamsMap[T] };
      const route = resolveEntityRoute(ref);
      if (route) navigate(route);
    },
    [navigate],
  );

  const navigateToEntityPath = useCallback(
    (path: string) => {
      const ref = parseEntityPath(path);
      if (ref) {
        const route = resolveEntityRoute(ref);
        if (route) {
          navigate(route);
          return;
        }
      }
      // Fallback: use path as-is
      navigate(path);
    },
    [navigate],
  );

  /**
   * Resolve a canonical entity path to a webapp route string without navigating.
   */
  const resolveEntityPath = useCallback((path: string): string => {
    const ref = parseEntityPath(path);
    if (ref) {
      const route = resolveEntityRoute(ref);
      if (route) return route;
    }
    return path;
  }, []);

  return { navigateToEntity, navigateToEntityPath, resolveEntityPath };
}
